/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useEffect } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled';
import { useAISurfaceMetadata } from '@atlassian/ai-smart-button-api';

import { useRovoButtonEventsInstrumentation } from '../../../../hooks/useRovoButtonEventsInstrumentation';
import { useDynamicSurfacePanel } from '../../../ai-dynamic-surface/useDynamicSurfacePanel';
import { useDebugNudgeTriggers } from '../../../common/hooks/use-debug-nudge-triggers';
import type { ObjectSidebar } from '../../../common/types/object-sidebar-types';
import type { LegacyAISmartButtonProps } from '../../../common/types/shared-types';
import { handleConvoStarters } from '../../../common/utils/ai-smart-button-shared';
import { useSharedAISmartButtonLogic } from '../../hooks/useSharedAISmartButtonLogic';
import { AISmartButtonUI } from '../ai-smart-button-ui';

const styles = cssMap({ wrapper: { position: 'relative' } });

/**
 * Use AISmartButton instead. This is a legacy component that uses the old object sidebar API.
 */
export const AISmartButtonLegacy = ({
	sidebarApi,
	isDisabled = false,
}: LegacyAISmartButtonProps) => {
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const surfaceMetadata = useAISurfaceMetadata();
	const { setShowConvoStarters, onAISmartButtonEnter, onAISmartButtonLeave } =
		useSharedAISmartButtonLogic();

	useDebugNudgeTriggers();

	useEffect(() => {
		fireRovoButtonUIEvent({
			source: surfaceMetadata?.contentType || 'AISmartButtonLegacy',
			action: 'rendered',
			actionSubject: 'rovoButton',
			actionSubjectId: 'aiSmartButton',
			attributes: {
				product: surfaceMetadata?.product,
				productPage: surfaceMetadata?.contentType,
				pageMode: surfaceMetadata?.pageMode,
				legacy: true,
			},
			product: surfaceMetadata?.product,
		});
	}, [fireRovoButtonUIEvent, surfaceMetadata]);

	const dynamicSurfacePanel = useDynamicSurfacePanel(sidebarApi as ObjectSidebar);

	const onAISmartButtonClick = React.useCallback(() => {
		if (!sidebarApi) {
			return;
		}

		if ((sidebarApi as ObjectSidebar).state.isObjectSidebarShown) {
			(sidebarApi as ObjectSidebar).actions.hideObjectSidebar();
		} else {
			(sidebarApi as ObjectSidebar).actions.showObjectSidebar(dynamicSurfacePanel, 'push');
			handleConvoStarters(surfaceMetadata, setShowConvoStarters);
		}

		fireRovoButtonUIEvent({
			source: surfaceMetadata?.contentType,
			action: 'clicked',
			actionSubject: 'rovoButton',
			actionSubjectId: 'aiRovoSmartButton',
			attributes: {
				product: surfaceMetadata?.product,
				productPage: surfaceMetadata?.contentType,
				pageMode: surfaceMetadata?.pageMode,
			},
			product: surfaceMetadata?.product,
		});
	}, [
		sidebarApi,
		dynamicSurfacePanel,
		surfaceMetadata,
		setShowConvoStarters,
		fireRovoButtonUIEvent,
	]);

	return (
		<Box xcss={styles.wrapper}>
			<AISmartButtonUI
				onClick={onAISmartButtonClick}
				isDisabled={isDisabled}
				onMouseEnter={onAISmartButtonEnter}
				onMouseLeave={onAISmartButtonLeave}
			/>
		</Box>
	);
};
